import React, { useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import moment from "moment";
import "./guideProfileGraph.css";
import annotationPlugin from "chartjs-plugin-annotation";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin
);

const GuideProfileGraph = ({ reviews }) => {
  const [filteredLanguage, setFilteredLanguage] = useState("All");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState(moment().format("YYYY-MM-DD"));

  // ORIGINAL PRODUCT CODES
  const productCodes = [
    ...new Set(reviews.map((review) => review[6]?.trim()).filter(Boolean)),
  ];

  // ⭐ NEW: PRODUCT CODES THAT SUPPORT OPTIONS
  const codesWithOptions = ["UKLO050", "FRPA049"];

  // ⭐ NEW: Extract option codes from review[8]
  const optionCodes = [
    ...new Set(
      reviews
        .map((r) => (r[8] ? String(r[8]).trim() : ""))
        .filter(
          (opt) =>
            opt &&
            opt !== "?" &&
            opt !== "-" &&
            codesWithOptions.some((main) => opt.startsWith(main))
        )
    ),
  ];

  // ⭐ NEW: Merge main product codes + option codes
  const mergedProductCodes = [
    ...new Set([...productCodes, ...optionCodes]),
  ];

  const languages = [
    ...new Set(
      reviews
        .map((review) => review[7]?.trim().toLowerCase())
        .filter((lang) => lang && lang !== "?")
    ),
  ];

  const showAllLanguages = languages.length > 1;

  const filteredReviews = reviews.filter(
    (review) =>
      (filteredLanguage === "All" ||
        review[7]?.trim().toLowerCase().includes(filteredLanguage)) &&
      (!fromDate ||
        moment(review[3], "DD/MM/YYYY").isSameOrAfter(moment(fromDate))) &&
      (!toDate ||
        moment(review[3], "DD/MM/YYYY").isSameOrBefore(moment(toDate)))
  );

  const getTimeRange = () => {
    if (!fromDate || !toDate) return "month";
    const diffDays = moment(toDate).diff(moment(fromDate), "days");
    if (diffDays <= 15) return "day";
    if (diffDays <= 90) return "week";
    return "month";
  };

  const computeData = (reviewsSubset) => {
    const data = {};
    reviewsSubset.forEach((review) => {
      const reviewDate = moment(review[3], "DD/MM/YYYY");
      const reviewKey =
        getTimeRange() === "day"
          ? reviewDate.format("YYYY-MM-DD")
          : getTimeRange() === "week"
          ? `${reviewDate.year()}-W${reviewDate.week()}`
          : reviewDate.format("YYYY-MM");

      if (!data[reviewKey]) {
        data[reviewKey] = {};
      }

      // ⭐ NEW: Choose correct code (option if exists)
      const baseCode = review[6]?.trim();
      const optCode = review[8] ? String(review[8]).trim() : "";
      const useCode =
        optCode &&
        codesWithOptions.some((main) => optCode.startsWith(main))
          ? optCode
          : baseCode;

      if (!data[reviewKey][useCode]) {
        data[reviewKey][useCode] = { sum: 0, count: 0 };
      }

      data[reviewKey][useCode].sum += review[4];
      data[reviewKey][useCode].count += 1;
    });
    return data;
  };

  const data = computeData(filteredReviews);

  const allAverages = [];

  Object.keys(data).forEach((key) => {
    Object.keys(data[key]).forEach((code) => {
      const avg = data[key][code].sum / data[key][code].count;
      allAverages.push(avg);
    });
  });

  let minYValue = Math.min(...allAverages);
  if (minYValue >= 4) {
    minYValue = 4;
  } else {
    minYValue = Math.floor(minYValue * 10) / 10;
  }

  // ⭐ UPDATED to use mergedProductCodes
  const productCodeDatasets = mergedProductCodes.map((code, index) => {
    const color = `hsl(${(index * 60) % 360}, 70%, 50%)`;
    const dataPoints = Object.keys(data).map((key) => {
      if (data[key][code]) {
        return (data[key][code].sum / data[key][code].count).toFixed(2);
      }
      return null;
    });
    
    const shapes = ["circle", "triangle", "rect"];

    return {
      label: code,
      data: dataPoints,
      borderColor: color,
      backgroundColor: `${color}33`,
      tension: 0.4,
      borderWidth: 1,
      pointRadius: 3,
      pointBackgroundColor: color,
      pointOffset: (index + 1) * 4,
      pointStyle: shapes[index % shapes.length],
    };
  });

  const chartData = {
    labels: Object.keys(data),
    datasets: productCodeDatasets,
  };

  const formatMonth = (tick) => {
    const [year, month] = tick.split("-");
    return `${new Date(year, month - 1).toLocaleString("default", {
      month: "short",
    })}'${year.slice(-2)}`;
  };

  const formatWeek = (tick) => {
    const [year, week] = tick.split("-W");
    return `Week ${week}, ${year}`;
  };

  const formatDate = (tick) => {
    const [year, month, day] = tick.split("-");
    return `${day}-${month}-${year}`;
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        ticks: {
          callback: function (value) {
            const tick = this.getLabelForValue(value);
            if (tick.includes("-W")) return formatWeek(tick);
            else if (tick.length === 7) return formatMonth(tick);
            else return formatDate(tick);
          },
        },
      },
      y: {
        ticks: {
          stepSize: 0.2,
        },
        min: minYValue,
        max: 5,
        afterDataLimits: (scale) => {
          scale.max += 0.05;
          scale.min += 0.05;
        },
      },
    },
    plugins: {
      legend: { display: false },
      annotation: {
        annotations: {
          backgroundHighlight: {
            type: "box",
            yMin: minYValue,
            yMax: 4.8,
            backgroundColor: "rgba(255, 92, 127, 0.19)",
            drawTime: "beforeDraw",
            borderWidth: 0,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const index = context.dataIndex;
            const dataset = context.dataset;
            const avg = dataset.data[index];
            const totalReviews =
              data[Object.keys(data)[index]]?.[dataset.label]?.count || 0;
            return `${dataset.label}: ${avg}, ${totalReviews} reviews`;
          },
        },
      },
    },
  };

  return (
    <div className="guide-profile-graph-container" style={{ width: "100%" }}>
      <h2 style={{ textAlign: "center", marginBottom: "10px" }}>
        Guide Review Graph
      </h2>

      <div className="filters" style={{ gap: "10px" }}>
        <div className="minreq">
          <div
            style={{
              width: "12px",
              height: "12px",
              backgroundColor: "rgba(255, 99, 133, 0.25)",
              marginRight: "5px",
              border: "1px solid rgba(255, 99, 132, 0.3)",
            }}
          ></div>
          <span style={{ fontSize: "12px" }}>
            (4.8) Minimum required average rating for a group tour guide
          </span>
        </div>

        <div className="lanlabel">
          <label>Languages</label>
          <select
            value={filteredLanguage}
            onChange={(e) => setFilteredLanguage(e.target.value)}
          >
            {showAllLanguages && <option value="All">All</option>}
            {languages.map((lang) => (
              <option key={lang} value={lang}>
                {lang}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="date-filter">
        <label htmlFor="fromDate">From: </label>
        <input
          id="fromDate"
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
        />
        <label htmlFor="toDate">To: </label>
        <input
          id="toDate"
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
        />
      </div>

      <div style={{ width: "100%", height: "60vh" }}>
        <Line data={chartData} options={chartOptions} className="lineGuide" />
      </div>

      <div
        className="legend-container"
        style={{
          marginTop: "10px",
          textAlign: "left",
          display: "flex",
          flexWrap: "wrap",
          gap: "10px",
        }}
      >
        {productCodeDatasets.map((dataset) => (
          <div
            key={dataset.label}
            style={{
              display: "flex",
              alignItems: "center",
              marginRight: "10px",
              fontSize: "12px",
            }}
          >
            <span
              style={{
                width: "10px",
                height: "10px",
                backgroundColor: dataset.borderColor,
                display: "inline-block",
                borderRadius: "50%",
                marginRight: "5px",
              }}
            ></span>
            {dataset.label}
          </div>
        ))}
      </div>
    </div>
  );
};

export default GuideProfileGraph;
